import { useMemo, useReducer, useState } from 'react'
import ForumRoundedIcon from '@mui/icons-material/ForumRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { FilterChip } from '../../Common/Chip'
import { TextInput } from '../../Common/TextInput'
import { Button } from '../../Common/Button'
import { IconButton } from '../../Common/IconButton'
import { Pressable } from '../../Common/Pressable'
import { useChat } from '../../../context/useChat'
import {
  buildChannelToken,
  buildMessageToken,
  chatSelectionReducer,
  chatSelectionInitialState,
  chatSourceContextToAttachment,
  materializeChatSelectionTokens,
  messageToChatSourceMessage,
} from '../../../types/aiChatSource'
import { capChatSelection, MAX_MESSAGES_PER_CHAT_CTX } from '../../../utils/chatContextCap'
import { useNls } from '../../../nls/nlsProvider'
import type { DataContext } from '../../../types/aiChatSource'
import type { Channel } from '../../../types/chat'

export interface ChatsPaneProps {
  basket: DataContext[]
  onToggle: (context: DataContext) => void
}

type ChatMode = 'whole' | 'messages' | 'range'

type DateRange = 'last24h' | 'last7d' | 'last30d' | 'custom'

const dateRangeToMs = (range: DateRange): number => {
  const now = Date.now()
  switch (range) {
    case 'last24h': return now - 24 * 60 * 60 * 1000
    case 'last7d': return now - 7 * 24 * 60 * 60 * 1000
    case 'last30d': return now - 30 * 24 * 60 * 60 * 1000
    default: return 0
  }
}

export const ChatsPane = ({ basket, onToggle }: ChatsPaneProps) => {
  const { t } = useNls()
  const { channelsList, messagesStream, activeChannelId, setActiveChannelId } = useChat()
  const [mode, setMode] = useState<ChatMode>('whole')
  const [messageSearch, setMessageSearch] = useState('')
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null)
  const [messageSelState, messageSelDispatch] = useReducer(chatSelectionReducer, chatSelectionInitialState)
  const [selectedRange, setSelectedRange] = useState<DateRange>('last7d')
  const [lastTruncationNotice, setLastTruncationNotice] = useState<{
    mode: ChatMode
    reason: string
    count: number
  } | null>(null)

  const dateRangeOptions = useMemo<{ value: DateRange; label: string }[]>(() => [
    { value: 'last24h', label: t('NLSAiContextPickerRangeLast24h') },
    { value: 'last7d', label: t('NLSAiContextPickerRangeLast7d') },
    { value: 'last30d', label: t('NLSAiContextPickerRangeLast30d') },
  ], [t])

  const activeChannels = useMemo(
    () => channelsList.filter((ch) => !ch.archivedAt && !ch.permanentlyDeletedAt),
    [channelsList],
  )

  // ---- Whole-channel mode ----
  const handleAttachWholeChannel = (channel: Channel) => {
    if (activeChannelId !== channel.id) {
      setActiveChannelId(channel.id)
      return
    }
    const messages = messagesStream.map(messageToChatSourceMessage)
    const { messages: capped, wasTruncated, reason } = capChatSelection(messages)
    const ctx = materializeChatSelectionTokens([buildChannelToken(channel)], {
      channels: channelsList,
      messagesByChannel: { [channel.id]: messagesStream },
    })[0]
    if (!ctx) return
    const attachment = chatSourceContextToAttachment({ ...ctx, messages: capped })
    onToggle({ ...attachment, kind: 'chat' } as DataContext)
    if (wasTruncated) {
      setLastTruncationNotice({ mode: 'whole', reason: reason ?? 'message_count', count: capped.length })
    } else {
      setLastTruncationNotice(null)
    }
  }

  // ---- Specific messages mode ----
  const selectChannel = (channelId: string) => {
    setSelectedChannelId(channelId)
    if (activeChannelId !== channelId) setActiveChannelId(channelId)
    messageSelDispatch({ type: 'clear' })
  }

  const filteredMessages = useMemo(() => {
    const q = messageSearch.trim().toLowerCase()
    const msgs = messagesStream.filter((m) => !m.isDeleted && m.textContent.trim())
    if (!q) return msgs
    return msgs.filter((m) => m.textContent.toLowerCase().includes(q) || m.authorName.toLowerCase().includes(q))
  }, [messagesStream, messageSearch])

  const handleSelectAllMessages = () => {
    filteredMessages.forEach((m) => {
      const channel = channelsList.find((ch) => ch.id === (selectedChannelId ?? activeChannelId))
      if (!channel) return
      const token = buildMessageToken(m, channel)
      if (!messageSelState.tokens.some((t) => 'messageId' in t && t.messageId === m.id)) {
        messageSelDispatch({ type: 'toggle-token', token })
      }
    })
  }

  const handleUnselectAllMessages = () => {
    messageSelDispatch({ type: 'clear' })
  }

  const handleAttachMessages = () => {
    const channel = channelsList.find((ch) => ch.id === (selectedChannelId ?? activeChannelId))
    if (!channel || messageSelState.tokens.length === 0) return
    const messages = messageSelState.tokens.map((t) => {
      if (t.kind !== 'message') return null
      const msg = messagesStream.find((m) => m.id === t.messageId)
      if (!msg) return null
      return messageToChatSourceMessage(msg)
    }).filter((m): m is NonNullable<typeof m> => m !== null)

    const { messages: capped, wasTruncated, reason } = capChatSelection(messages)
    const ctx = materializeChatSelectionTokens(messageSelState.tokens, {
      channels: channelsList,
      messagesByChannel: { [channel.id]: messagesStream },
    })[0]
    if (!ctx) return
    const attachment = chatSourceContextToAttachment({ ...ctx, messages: capped })
    onToggle({ ...attachment, kind: 'chat' } as DataContext)
    messageSelDispatch({ type: 'clear' })
    if (wasTruncated) {
      setLastTruncationNotice({ mode: 'messages', reason: reason ?? 'message_count', count: capped.length })
    } else {
      setLastTruncationNotice(null)
    }
  }

  // ---- Date range mode ----
  const handleAttachRange = (channel: Channel) => {
    const sinceMs = dateRangeToMs(selectedRange)
    if (activeChannelId !== channel.id) {
      setActiveChannelId(channel.id)
      return
    }
    const rangeMessages = messagesStream
      .filter((m) => {
        const ts = typeof m.timestamp === 'number'
          ? m.timestamp
          : m.timestamp && typeof m.timestamp === 'object' && 'toMillis' in m.timestamp
            ? (m.timestamp as { toMillis(): number }).toMillis()
            : 0
        return ts >= sinceMs && !m.isDeleted && m.textContent.trim()
      })
      .map(messageToChatSourceMessage)
    const { messages: capped, wasTruncated, reason } = capChatSelection(rangeMessages)
    const ctx = materializeChatSelectionTokens(
      [{ kind: 'channel', channelId: channel.id, channelName: channel.slug || channel.name, sinceMs }],
      { channels: channelsList, messagesByChannel: { [channel.id]: messagesStream } },
    )[0]
    if (!ctx) return
    const attachment = chatSourceContextToAttachment({ ...ctx, messages: capped })
    onToggle({ ...attachment, kind: 'chat' } as DataContext)
    if (wasTruncated) {
      setLastTruncationNotice({ mode: 'range', reason: reason ?? 'message_count', count: capped.length })
    } else {
      setLastTruncationNotice(null)
    }
  }

  return (
    <div className="ctxPane ctxPane--chats">
      {/* Segmented control */}
      <div className="ctxPane__toolbar ctxPane__toolbar--segmented">
        {(['whole', 'messages', 'range'] as ChatMode[]).map((m) => (
          <Pressable
            key={m}
            className={`ctxPane__segBtn${mode === m ? ' is-active' : ''}`}
            aria-pressed={mode === m}
            onClick={() => {
              setMode(m)
              setLastTruncationNotice(null)
            }}
          >
            {m === 'whole'
              ? t('NLSAiContextPickerChatModeWhole')
              : m === 'messages'
                ? t('NLSAiContextPickerChatModeMessages')
                : t('NLSAiContextPickerChatModeRange')}
          </Pressable>
        ))}
      </div>

      {/* Post-attach truncation notice */}
      {lastTruncationNotice && (
        <div className="ctxPane__capError" style={{ margin: '8px 16px 0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ErrorOutlineRoundedIcon style={{ fontSize: 16, flexShrink: 0 }} />
            <span>
              {lastTruncationNotice.reason === 'token_budget'
                ? t('NLSAiContextPickerCapBlocked')
                : t('NLSAiContextPickerCapTruncated', { n: lastTruncationNotice.count })}
            </span>
          </div>
          <IconButton
            size="sm"
            variant="tertiary"
            onClick={() => setLastTruncationNotice(null)}
            label={t('NLSAiContextPickerAriaDismissNotice')}
            icon={<CloseRoundedIcon fontSize="small" />}
          />
        </div>
      )}

      {/* Whole channel mode */}
      {mode === 'whole' && (
        <div className="ctxPane__channelList">
          {activeChannels.length === 0 ? (
            <div className="ctxPane__empty">
              <ForumRoundedIcon style={{ fontSize: 36, opacity: 0.3 }} />
              <p>{t('NLSAiContextPickerNoChannels')}</p>
            </div>
          ) : (
            activeChannels.map((channel) => {
              const isLoaded = activeChannelId === channel.id
              // Dedup by channelId for chat contexts (see chatSelectionTokenKey in aiChatSource.ts for canonical tokens).
              const contextId = basket.find(
                (b) => b.channelId === channel.id && b.kind === 'chat',
              )?.id
              const isInBasket = Boolean(contextId)
              const msgCount = isLoaded ? messagesStream.length : null
              const willTruncate = msgCount !== null && msgCount > MAX_MESSAGES_PER_CHAT_CTX

              return (
                <div key={channel.id} className={`ctxPane__channelRow${isInBasket ? ' is-selected' : ''}`}>
                  <div className="ctxPane__channelInfo">
                    <strong>#{channel.slug || channel.name}</strong>
                    <small>
                      {isLoaded
                        ? t('NLSAiContextPickerMessagesLoaded', { count: messagesStream.length })
                        : t('NLSAiContextPickerLoadHint')}
                    </small>
                    {willTruncate && (
                      <span className="ctxPane__capWarning">
                        <WarningAmberRoundedIcon style={{ fontSize: 12 }} />
                        {t('NLSAiContextPickerWillTrim', { n: MAX_MESSAGES_PER_CHAT_CTX })}
                      </span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    pressed={isInBasket}
                    onClick={() => handleAttachWholeChannel(channel)}
                  >
                    {isInBasket ? t('NLSAiContextPickerAttached') : isLoaded ? t('NLSAiContextPickerAttach') : t('NLSAiContextPickerLoadAndAttach')}
                  </Button>
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Specific messages mode */}
      {mode === 'messages' && (
        <div className="ctxPane__messageMode">
          {!selectedChannelId ? (
            <div className="ctxPane__channelList">
              <div className="ctxPane__hintText">{t('NLSAiContextPickerSelectChannelHint')}</div>
              {activeChannels.map((channel) => (
                <div
                  key={channel.id}
                  className="ctxPane__channelRow ctxPane__channelRow--clickable"
                  onClick={() => selectChannel(channel.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') selectChannel(channel.id) }}
                >
                  <div className="ctxPane__channelInfo">
                    <strong>#{channel.slug || channel.name}</strong>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="ctxPane__toolbar">
                <Button
                  size="sm"
                  variant="tertiary"
                  onClick={() => setSelectedChannelId(null)}
                >
                  ← {t('NLSAiComposerBack')}
                </Button>
                <TextInput
                  type="search"
                  leadingIcon={<SearchRoundedIcon fontSize="small" />}
                  placeholder={t('NLSAiContextPickerSearchFilterMessages')}
                  value={messageSearch}
                  onChange={setMessageSearch}
                  aria-label={t('NLSAiContextPickerSearchFilterMessages')}
                />
                <div className="ctxPane__selectActions">
                  <Button
                    size="sm"
                    variant="tertiary"
                    onClick={handleSelectAllMessages}
                    disabled={filteredMessages.length === 0}
                  >
                    {t('NLSAiContextPickerSelectAll')}
                  </Button>
                  <Button
                    size="sm"
                    variant="tertiary"
                    onClick={handleUnselectAllMessages}
                    disabled={messageSelState.tokens.length === 0}
                  >
                    {t('NLSAiContextPickerUnselectAll')}
                  </Button>
                </div>
              </div>

              {activeChannelId !== selectedChannelId ? (
                <div className="ctxPane__loader">{t('NLSAiContextPickerLoadingMessages')}</div>
              ) : filteredMessages.length === 0 ? (
                <div className="ctxPane__empty">
                  <p>{t('NLSAiContextPickerNoMessages')}</p>
                </div>
              ) : (
                <div className="ctxPane__messageList">
                  {filteredMessages.slice(-100).map((msg) => {
                    const channel = channelsList.find((ch) => ch.id === selectedChannelId)!
                    const token = channel ? buildMessageToken(msg, channel) : null
                    const isSelected = token
                      ? messageSelState.tokens.some((t) => 'messageId' in t && t.messageId === msg.id)
                      : false
                    return (
                      <div
                        key={msg.id}
                        className={`ctxPane__messageRow${isSelected ? ' is-selected' : ''}`}
                        onClick={() => {
                          if (!token) return
                          messageSelDispatch({ type: 'toggle-token', token })
                        }}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault()
                            if (token) messageSelDispatch({ type: 'toggle-token', token })
                          }
                        }}
                      >
                        <div className="ctxPane__responseCheckbox">
                          {isSelected && <CheckRoundedIcon style={{ fontSize: 12 }} />}
                        </div>
                        <div className="ctxPane__messageInfo">
                          <strong>{msg.authorName}</strong>
                          <small>{msg.textContent.replace(/\s+/g, ' ').trim().slice(0, 80)}</small>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {messageSelState.tokens.length > MAX_MESSAGES_PER_CHAT_CTX && (
                <div className="ctxPane__capError">
                  <ErrorOutlineRoundedIcon style={{ fontSize: 14 }} />
                  {t('NLSAiContextPickerCapTruncated', { n: MAX_MESSAGES_PER_CHAT_CTX })}
                </div>
              )}

              <div className="ctxPane__drillFooter">
                <span className="ctxPane__drillCount">
                  {messageSelState.tokens.length === 1
                    ? t('NLSAiContextPickerMessageSelectedOne')
                    : t('NLSAiContextPickerMessagesSelected', { count: messageSelState.tokens.length })}
                </span>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleAttachMessages}
                  disabled={messageSelState.tokens.length === 0}
                >
                  {t('NLSAiContextPickerAttach')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Date range mode */}
      {mode === 'range' && (
        <div className="ctxPane__rangeMode">
          <div className="ctxPane__rangeControls">
            {dateRangeOptions.map((opt) => (
              <FilterChip
                key={opt.value}
                selected={selectedRange === opt.value}
                onClick={() => setSelectedRange(opt.value)}
              >
                {opt.label}
              </FilterChip>
            ))}
          </div>
          <div className="ctxPane__channelList">
            {activeChannels.map((channel) => {
              const isLoaded = activeChannelId === channel.id
              const sinceMs = dateRangeToMs(selectedRange)
              const rangeCount = isLoaded
                ? messagesStream.filter((m) => {
                    const ts = typeof m.timestamp === 'number'
                      ? m.timestamp
                      : m.timestamp && typeof m.timestamp === 'object' && 'toMillis' in m.timestamp
                        ? (m.timestamp as { toMillis(): number }).toMillis()
                        : 0
                    return ts >= sinceMs && !m.isDeleted
                  }).length
                : null

              return (
                <div key={channel.id} className="ctxPane__channelRow">
                  <div className="ctxPane__channelInfo">
                    <strong>#{channel.slug || channel.name}</strong>
                    <small>
                      {rangeCount !== null
                        ? t('NLSAiContextPickerMessagesInRange', { count: rangeCount })
                        : t('NLSAiContextPickerClickToLoad')}
                    </small>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleAttachRange(channel)}
                  >
                    {isLoaded ? t('NLSAiContextPickerAttachRange') : t('NLSAiContextPickerLoadAndAttach')}
                  </Button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
